import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Loader2, Trash2 } from "lucide-react";

import { DELETE_ACCOUNT } from "@/graphql/mutations/account";
import { useAuth } from "@/lib/auth/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CONFIRM_WORD = "IŠTRINTI";

type DeleteAccountResult = {
  deleteAccount: { success: boolean; emailed: boolean; errors: string[] };
};

/**
 * „Ištrinti paskyrą" — a grown-up deleting their own account (GDPR art. 17).
 *
 * The confirmation is typed, not clicked: the word or the account's own
 * address. A button in a dialog is pressed by reflex; typing a word is not, and
 * nothing here can be undone. The server checks the same thing, so the button
 * being enabled is a convenience and never the gate.
 *
 * What goes is stated before anything is typed — the children's profiles above
 * all, because that is the part a parent may not expect.
 */
export function DeleteAccountCard() {
  const { email } = useAuth();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [deleteAccount, { loading }] = useMutation<DeleteAccountResult>(DELETE_ACCOUNT);

  const value = typed.trim();
  const matches =
    value.toLocaleUpperCase("lt") === CONFIRM_WORD ||
    (!!email && value.toLowerCase() === email.toLowerCase());

  const close = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setTyped("");
      setError(null);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!matches) return;
    setError(null);

    try {
      const { data } = await deleteAccount({ variables: { confirmation: value } });
      const result = data?.deleteAccount;

      if (result?.success) {
        // The cookie is already gone; a full load leaves nothing of the old
        // account in the Apollo cache.
        window.location.assign(`/auth/login?deleted=1${result.emailed ? "&emailed=1" : ""}`);
        return;
      }
      setError(result?.errors?.[0] || "Paskyros ištrinti nepavyko");
    } catch {
      setError("Paskyros ištrinti nepavyko");
    }
  };

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle>Ištrinti paskyrą</CardTitle>
        <CardDescription>
          Visam laikui ištrina jūsų paskyrą. Atšaukti negalima.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
          <li>
            Bus ištrinti vaikų profiliai, kurių neprižiūri kitas šeimos suaugęs, su
            visa jų pažanga, sesijomis ir pokalbiais.
          </li>
          <li>
            Jei vaiką prižiūri ir kitas suaugęs, vaikas lieka jam — ištrinama tik
            jūsų paskyra.
          </li>
          <li>Prenumerata, jei ją turite, bus atšaukta iš karto.</li>
          <li>
            Mokėjimų įrašai lieka tik tiek, kiek reikia apskaitai, be jūsų vardo ir
            el. pašto.
          </li>
        </ul>
        <p className="text-muted-foreground">
          Jei esate šeimos savininkas, o šeimoje yra kitas suaugęs, pirma
          perleiskite jam šeimą puslapyje „Šeima“.
        </p>
        <Button variant="destructive" onClick={() => setOpen(true)}>
          <Trash2 className="mr-2 h-4 w-4" />
          Ištrinti paskyrą
        </Button>
      </CardContent>

      <Dialog open={open} onOpenChange={close}>
        <DialogContent>
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Ar tikrai ištrinti paskyrą?</DialogTitle>
              <DialogDescription>
                Įrašykite žodį <strong>{CONFIRM_WORD}</strong>
                {email ? (
                  <>
                    {" "}arba savo el. pašto adresą (<strong>{email}</strong>)
                  </>
                ) : null}
                .
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="delete-account-confirmation">Patvirtinimas</Label>
              <Input
                id="delete-account-confirmation"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                autoFocus
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)}>
                Atšaukti
              </Button>
              <Button type="submit" variant="destructive" disabled={!matches || loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Ištrinti visam laikui
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
